import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PartBinListItem } from "../../../@types/DRO/GetAllBinsResponse";
import { GetAllProductListApi } from "../../../api/GetProductList/GetAllProductsListApi";
import { WarehouseBinsApi } from "../../../api/WarehouseBins/WarehouseBinsApi";
import SideBarNavigation from "../../Layouts/Sidebar/Sidebar";

type BinStatus = "Active" | "Inactive";

interface BinProductRow {
  id: string;
  productName: string;
  productCode: string;
  referenceCode: string;
  quantity: number;
  warehouseId: number;
  binId?: number;
  partId?: number;
  binName?: string;
}

interface MoveBinRow {
  rowId: string;
  binId: string;
  quantity: string;
}

interface MovePartsModalData {
  productCode: string;
  productName: string;
  referenceCode: string;
  quantity: number;
  warehouseId: number;
  partId?: number;
  sourceBinName?: string;
  sourceBinId?: number;
}

interface BinRow {
  id: number;
  binName: string;
  binId?: number;
  partsCount: number;
  createdAt: string;
  city: string;
  status: BinStatus;
  products?: BinProductRow[];
  warehouseId: number;
}

const ROWS_PER_PAGE = 10;
const ACTIVE_BINS_PAGE_SIZE = 8;

type ActiveBinOption = {
  id: number;
  binName: string;
  warehouseId: number | null;
  warehouseName?: string | null;
  active: boolean;
};

interface ScrollableBinSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: ActiveBinOption[];
  isLoading: boolean;
  isLoadingMore: boolean;
  isSearching: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  excludeBinId?: number;
  error?: string | null;
}

const ScrollableBinSelect = ({
  value,
  onChange,
  options,
  isLoading,
  isLoadingMore,
  isSearching,
  hasMore,
  onLoadMore,
  searchQuery,
  onSearchChange,
  excludeBinId,
  error,
}: ScrollableBinSelectProps) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => searchInputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open]);

  const filteredOptions = options.filter(
    (bin) => bin.active && bin.id !== excludeBinId
  );
  const selected = options.find((bin) => String(bin.id) === value);

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const node = event.currentTarget;
    const nearBottom = node.scrollHeight - node.scrollTop - node.clientHeight <= 12;
    if (nearBottom && hasMore && !isLoadingMore && !isLoading && !isSearching) {
      onLoadMore();
    }
  };

  useEffect(() => {
    if (!open || isLoading || isLoadingMore || isSearching || !hasMore || filteredOptions.length === 0) {
      return;
    }

    const frame = requestAnimationFrame(() => {
      const node = listRef.current;
      if (!node) {
        return;
      }
      if (node.scrollHeight <= node.clientHeight + 12) {
        onLoadMore();
      }
    });

    return () => cancelAnimationFrame(frame);
  }, [open, filteredOptions.length, isLoading, isLoadingMore, isSearching, hasMore, onLoadMore]);

  return (
    <div ref={containerRef} className="relative flex flex-col gap-2">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        disabled={isLoading && !selected}
        className="w-full border-0 border-b border-slate-200 bg-transparent pb-1 text-left text-sm text-slate-800 outline-none focus:border-blue-500 disabled:text-slate-400">
        {isLoading && !selected
          ? "Loading bins..."
          : selected
            ? `${selected.binName} (${selected.warehouseId}${selected.warehouseName ? ` - ${selected.warehouseName}` : ""})`
            : "Select Bin"}
      </button>
      {open && (
        <div className="absolute left-0 right-0 top-full z-20 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg">
          <div className="border-b border-slate-100 p-2">
            <div className="relative">
              <svg
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor">
                <circle cx="11" cy="11" r="7" strokeWidth={2} />
                <path d="M20 20l-3.5-3.5" strokeWidth={2} strokeLinecap="round" />
              </svg>
              <input
                ref={searchInputRef}
                value={searchQuery}
                onChange={(event) => onSearchChange(event.target.value)}
                placeholder="Search bins"
                className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-9 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />
              {isSearching && (
                <svg
                  className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-slate-400"
                  viewBox="0 0 24 24"
                  fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
              )}
            </div>
          </div>
          <div
            ref={listRef}
            className={`max-h-44 overflow-y-auto py-1 transition-opacity ${
              isSearching ? "pointer-events-none opacity-60" : ""
            }`}
            onScroll={handleScroll}>
            {isLoading && filteredOptions.length === 0 ? (
              <p className="px-3 py-2 text-sm text-slate-500">Loading bins...</p>
            ) : filteredOptions.length > 0 ? (
              filteredOptions.map((bin) => (
                <button
                  key={bin.id}
                  type="button"
                  onClick={() => {
                    onChange(String(bin.id));
                    setOpen(false);
                  }}
                  className={`block w-full px-3 py-2 text-left text-sm transition hover:bg-blue-50 ${
                    value === String(bin.id) ? "bg-blue-50 font-medium text-blue-700" : "text-slate-800"
                  }`}>
                  <span className="font-medium">{bin.binName}</span>
                  <span className="ml-1 text-slate-500">
                    ({bin.warehouseId}
                    {bin.warehouseName ? ` - ${bin.warehouseName}` : ""})
                  </span>
                </button>
              ))
            ) : (
              <p className="px-3 py-2 text-sm text-slate-500">
                {searchQuery.trim() ? "No bins match your search." : "No bins available."}
              </p>
            )}
            {isLoadingMore && (
              <p className="px-3 py-2 text-center text-xs text-slate-500">Loading more bins...</p>
            )}
          </div>
        </div>
      )}
      {error && <p className="text-xs text-rose-600">{error}</p>}
    </div>
  );
};

type InitialBin = { id: number; binName: string; warehouseId: number };

type Props = {
  initialBins?: InitialBin[];
  initialProducts?: any[];
};

const ManageBinsPage = ({ initialBins, initialProducts }: Props) => {
  const [statusFilter, setStatusFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [defaultWarehouseIdLabel, setDefaultWarehouseIdLabel] = useState("-");
  const [defaultWarehouseNameLabel, setDefaultWarehouseNameLabel] = useState("-");
  const [expandedBinId, setExpandedBinId] = useState<number | null>(null);
  const [rows, setRows] = useState<BinRow[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [listRefreshTrigger, setListRefreshTrigger] = useState(0);
  const [movePartsData, setMovePartsData] = useState<MovePartsModalData | null>(null);
  const [moveBinRows, setMoveBinRows] = useState<MoveBinRow[]>([]);
  const [binOptions, setBinOptions] = useState<ActiveBinOption[]>([]);
  const [isBinsLoading, setIsBinsLoading] = useState(false);
  const [isActiveBinsLoadingMore, setIsActiveBinsLoadingMore] = useState(false);
  const [activeBinsPage, setActiveBinsPage] = useState(0);
  const [activeBinsTotalPages, setActiveBinsTotalPages] = useState(1);
  const [activeBinsError, setActiveBinsError] = useState<string | null>(null);
  const [moveModalBinSearchQuery, setMoveModalBinSearchQuery] = useState("");
  const [debouncedMoveModalBinSearch, setDebouncedMoveModalBinSearch] = useState("");
  const [isActiveBinsSearching, setIsActiveBinsSearching] = useState(false);
  const moveModalBinsFetchIdRef = useRef(0);
  const hasMoveModalBinsLoadedRef = useRef(false);
  const [isMovingParts, setIsMovingParts] = useState(false);
  const [movePartsError, setMovePartsError] = useState<string | null>(null);
  const [successToast, setSuccessToast] = useState<string | null>(null);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [isUploadingBulk, setIsUploadingBulk] = useState(false);
  const [bulkUploadError, setBulkUploadError] = useState<string | null>(null);
  const bulkFileInputRef = useRef<HTMLInputElement>(null);
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const [expandedProducts, setExpandedProducts] = useState<BinProductRow[] | null>(null);
  const [expandedLoading, setExpandedLoading] = useState(false);
  const [expandedLoadError, setExpandedLoadError] = useState<string | null>(null);

  const handleDownloadTemplate = async () => {
    setIsDownloadingTemplate(true);
    try {
      const blob = await WarehouseBinsApi.api.downloadWarehouseBins();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "part-bin-mapping.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setBulkUploadError("Failed to download template.");
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  useEffect(() => {
    if (!successToast) return;
    const timer = setTimeout(() => setSuccessToast(null), 2500);
    return () => clearTimeout(timer);
  }, [successToast]);

  const [debouncedSearch, setDebouncedSearch] = useState<string>("");

  // debounce search input to avoid too many API calls
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    const fetchRows = async () => {
      setIsLoading(true);
      setLoadError(null);

      try {
        const response = await WarehouseBinsApi.api.getPartBinList(
          currentPage,
          ROWS_PER_PAGE,
          debouncedSearch,
          statusFilter === "All" ? undefined : statusFilter.toLowerCase()
        );

        setDefaultWarehouseIdLabel(
          response.data.defaultWarehouseId !== undefined &&
            response.data.defaultWarehouseId !== null
            ? String(response.data.defaultWarehouseId)
            : "-"
        );
        setDefaultWarehouseNameLabel(
          response.data.defaultWarehouseName !== undefined &&
            response.data.defaultWarehouseName !== null
            ? String(response.data.defaultWarehouseName)
            : "-"
        );

        const mappedRows: BinRow[] = (response.data.content ?? []).map(
          (item: PartBinListItem, index: number) => ({
            id: index + 1,
            binId: item.binId,
            binName: item.binName,
            partsCount: item.partsCount ?? 0,
            createdAt: item.createdAt ?? "-",
            city: item.warehouseName,
            status: item.active ? "Active" : "Inactive",
            warehouseId: item.warehouseId,
          })
        );

        setRows(mappedRows);
        setTotalPages(response.data.totalPages || 1);
      } catch (error) {
        console.error("Error fetching part bins", error);
        setRows([]);
        setTotalPages(1);
        setLoadError("Unable to load bins. Please try again.");
        setDefaultWarehouseIdLabel("-");
        setDefaultWarehouseNameLabel("-");
      } finally {
        setIsLoading(false);
      }
    };

    fetchRows();
  }, [currentPage, debouncedSearch, statusFilter, listRefreshTrigger]);

  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearch, statusFilter]);

  useEffect(() => {
    setExpandedBinId(null);
    setExpandedProducts(null);
    setExpandedLoadError(null);
    setExpandedLoading(false);
  }, [currentPage]);

  const pageNumbers = useMemo(
    () => Array.from({ length: totalPages }, (_, index) => index + 1),
    [totalPages]
  );

  const filteredRows = rows;

  useEffect(() => {
    const handler = setTimeout(
      () => setDebouncedMoveModalBinSearch(moveModalBinSearchQuery.trim()),
      300
    );
    return () => clearTimeout(handler);
  }, [moveModalBinSearchQuery]);

  const fetchMoveModalActiveBins = useCallback(
    async (page: number, append: boolean, search: string) => {
      const response = await WarehouseBinsApi.api.getAllActiveBinsList(
        page,
        ACTIVE_BINS_PAGE_SIZE,
        search || undefined
      );
      const mapped: ActiveBinOption[] = (response.data.data ?? []).map((bin) => ({
        id: bin.id,
        binName: bin.binName,
        warehouseId: bin.warehouseId,
        warehouseName: bin.warehouseName,
        active: bin.active,
      }));

      setActiveBinsPage(response.data.pageNumber ?? page);
      setActiveBinsTotalPages(response.data.totalPages || 1);
      setBinOptions((prev) => (append ? [...prev, ...mapped] : mapped));
    },
    []
  );

  useEffect(() => {
    if (!movePartsData) {
      return;
    }
    if (initialBins && initialBins.length > 0) {
      return;
    }

    const fetchId = ++moveModalBinsFetchIdRef.current;
    const isInitialFetch = !hasMoveModalBinsLoadedRef.current;

    if (isInitialFetch) {
      setIsBinsLoading(true);
      setActiveBinsError(null);
      setBinOptions([]);
      setActiveBinsPage(0);
      setActiveBinsTotalPages(1);
    } else {
      setIsActiveBinsSearching(true);
    }

    const fetchActiveBins = async () => {
      try {
        await fetchMoveModalActiveBins(0, false, debouncedMoveModalBinSearch);
        if (fetchId !== moveModalBinsFetchIdRef.current) {
          return;
        }
        hasMoveModalBinsLoadedRef.current = true;
      } catch {
        if (fetchId !== moveModalBinsFetchIdRef.current) {
          return;
        }
        setActiveBinsError("Failed to load active bins. Please try again.");
        if (isInitialFetch) {
          setBinOptions([]);
          setActiveBinsPage(0);
          setActiveBinsTotalPages(1);
        }
      } finally {
        if (fetchId === moveModalBinsFetchIdRef.current) {
          setIsBinsLoading(false);
          setIsActiveBinsSearching(false);
        }
      }
    };

    void fetchActiveBins();
  }, [movePartsData, debouncedMoveModalBinSearch, initialBins, fetchMoveModalActiveBins]);

  const handleLoadMoreActiveBins = useCallback(async () => {
    if (
      isBinsLoading ||
      isActiveBinsLoadingMore ||
      isActiveBinsSearching ||
      activeBinsPage >= activeBinsTotalPages - 1
    ) {
      return;
    }

    const nextPage = activeBinsPage + 1;
    setIsActiveBinsLoadingMore(true);
    setActiveBinsError(null);
    try {
      await fetchMoveModalActiveBins(nextPage, true, debouncedMoveModalBinSearch);
    } catch {
      setActiveBinsError("Failed to load more active bins. Please try again.");
    } finally {
      setIsActiveBinsLoadingMore(false);
    }
  }, [
    isBinsLoading,
    isActiveBinsLoadingMore,
    isActiveBinsSearching,
    activeBinsPage,
    activeBinsTotalPages,
    debouncedMoveModalBinSearch,
    fetchMoveModalActiveBins,
  ]);

  const handleOpenMoveParts = (product: BinProductRow, parentBinName?: string) => {
    setMovePartsData({
      productCode: product.productCode,
      productName: product.productName,
      referenceCode: product.referenceCode,
      quantity: product.quantity,
      warehouseId: product.warehouseId,
      partId: product.partId,
      sourceBinId: product.binId,
      sourceBinName: parentBinName ?? product.binName,
    });
    setMoveBinRows([{ rowId: "1", binId: "", quantity: "" }]);
    setMovePartsError(null);
    setActiveBinsError(null);
    setMoveModalBinSearchQuery("");
    setDebouncedMoveModalBinSearch("");
    setActiveBinsPage(0);
    setActiveBinsTotalPages(1);
    setIsActiveBinsLoadingMore(false);
    setIsActiveBinsSearching(false);
    moveModalBinsFetchIdRef.current += 1;
    hasMoveModalBinsLoadedRef.current = false;

    if (initialBins && initialBins.length > 0) {
      const mapped = initialBins.map((b) => ({
        id: b.id,
        binName: b.binName,
        warehouseId: b.warehouseId,
        active: true,
      }));
      setBinOptions(mapped);
      setIsBinsLoading(false);
      return;
    }

    setBinOptions([]);
  };

  // -------- Add Product to Bin modal state & handlers --------
  const [isAddProductOpen, setIsAddProductOpen] = useState(false);
  const [addProductBin, setAddProductBin] = useState<BinRow | null>(null);
  const [productOptions, setProductOptions] = useState<any[]>([]);
  const [isProductsLoading, setIsProductsLoading] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string>("");
  const [addQuantity, setAddQuantity] = useState<string>("");
  const [isAddingProduct, setIsAddingProduct] = useState(false);
  const [addProductError, setAddProductError] = useState<string | null>(null);

  const handleOpenAddProduct = async (bin: BinRow) => {
    setAddProductBin(bin);
    setIsAddProductOpen(true);
    setSelectedProductId("");
    setAddQuantity("");
    setAddProductError(null);
    setIsProductsLoading(true);
    if (initialProducts && initialProducts.length > 0) {
      setProductOptions(initialProducts);
      setIsProductsLoading(false);
      return;
    }

    try {
      const resp = await GetAllProductListApi.api.getAllProductsList(bin.warehouseId);
      setProductOptions(resp.data ?? []);
    } catch (err) {
      setProductOptions([]);
    } finally {
      setIsProductsLoading(false);
    }
  };

  const handleCloseAddProduct = () => {
    setIsAddProductOpen(false);
    setAddProductBin(null);
    setProductOptions([]);
    setSelectedProductId("");
    setAddQuantity("");
    setAddProductError(null);
  };

  const handleSubmitAddProduct = async () => {
    if (!addProductBin) return;
    if (!selectedProductId || !addQuantity || Number(addQuantity) <= 0) {
      setAddProductError("Please select a product and enter a valid quantity.");
      return;
    }

    setIsAddingProduct(true);
    setAddProductError(null);
    try {
      let found: any | undefined;
      if (initialBins && initialBins.length > 0) {
        found = initialBins.find(
          (b) => b.binName === addProductBin.binName && b.warehouseId === addProductBin.warehouseId
        );
      }

      if (!found) {
        found = rows.find(
          (row) => row.binName === addProductBin.binName && row.warehouseId === addProductBin.warehouseId
        );
      }

      if (!found) {
        setAddProductError("Unable to resolve bin id. Please try again.");
        setIsAddingProduct(false);
        return;
      }

      const resolvedBinId = Number((found as any).binId ?? (found as any).id);

      if (!resolvedBinId || isNaN(resolvedBinId)) {
        setAddProductError("Please select a valid bin before adding a product.");
        setIsAddingProduct(false);
        return;
      }

      await WarehouseBinsApi.api.createPartBin({
        partId: Number(selectedProductId),
        warehouse: addProductBin.warehouseId,
        binId: resolvedBinId,
        quantity: Number(addQuantity),
      });

      setSuccessToast("Product added to bin!");
      handleCloseAddProduct();
      setListRefreshTrigger((prev) => prev + 1);

      try {
        if (expandedBinId !== null) {
          const expandedRow = rows.find((r) => r.id === expandedBinId);
          if (expandedRow && expandedRow.binId) {
            setExpandedLoading(true);
            const resp = await WarehouseBinsApi.api.getExpandedBinParts(Number(expandedRow.binId));
            let parts: any[] = [];
            if (Array.isArray(resp)) {
              parts = resp;
            } else if (Array.isArray(resp?.data)) {
              parts = resp.data;
            } else if (Array.isArray(resp?.data?.data)) {
              parts = resp.data.data;
            } else if (Array.isArray(resp?.content)) {
              parts = resp.content;
            } else if (Array.isArray(resp?.data?.content)) {
              parts = resp.data.content;
            }

            const products = (parts || []).map((item: any, idx: number) => ({
              id: `${expandedRow.binId}-${idx}`,
              productName: item.partName ?? item.productName ?? "",
              productCode: item.referenceCode ?? item.productCode ?? "",
              referenceCode: item.referenceCode ?? item.productCode ?? "",
              quantity: item.quantity ?? 0,
              warehouseId: item.warehouseId ?? expandedRow.warehouseId,
              binId: expandedRow.binId,
              partId: item.partId ?? item.id,
              binName: expandedRow.binName,
            }));

            setExpandedProducts(products);
            setExpandedLoadError(null);
            setExpandedLoading(false);
          }
        }
      } catch {
        setExpandedLoadError("Failed to refresh expanded bin details.");
        setExpandedLoading(false);
      }
    } catch (err: any) {
      const backendMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to add product. Please try again.";
      setAddProductError(backendMessage);
    } finally {
      setIsAddingProduct(false);
    }
  };

  const handleCloseMoveParts = () => {
    setMovePartsData(null);
    setMoveBinRows([]);
    setBinOptions([]);
    setMovePartsError(null);
    setActiveBinsError(null);
    setMoveModalBinSearchQuery("");
    setDebouncedMoveModalBinSearch("");
    setActiveBinsPage(0);
    setActiveBinsTotalPages(1);
    setIsActiveBinsLoadingMore(false);
    setIsActiveBinsSearching(false);
    moveModalBinsFetchIdRef.current += 1;
    hasMoveModalBinsLoadedRef.current = false;
  };

  const handleAddBinRow = () => {
    setMoveBinRows((prev) => [
      ...prev,
      { rowId: String(Date.now()), binId: "", quantity: "" },
    ]);
  };

  const handleRemoveBinRow = (rowId: string) => {
    setMoveBinRows((prev) => prev.filter((row) => row.rowId !== rowId));
  };

  const handleBinRowChange = (
    rowId: string,
    field: "binId" | "quantity",
    value: string
  ) => {
    // sanitize quantity to allow only integer values (no decimals)
    // keep only the integer portion before any decimal point and strip non-digits
    const sanitized =
      field === "quantity" ? String(value).split(".")[0].replace(/\D/g, "") : value;
    setMoveBinRows((prev) =>
      prev.map((row) => (row.rowId === rowId ? { ...row, [field]: sanitized } : row))
    );
  };

  const handleMoveParts = async () => {
    if (!movePartsData) return;
    const validRows = moveBinRows.filter(
      (row) => row.binId && row.quantity && Number(row.quantity) > 0
    );
    if (validRows.length === 0) {
      setMovePartsError("Please select at least one bin and enter a quantity.");
      return;
    }
    setIsMovingParts(true);
    setMovePartsError(null);
    try {
      if (!movePartsData.sourceBinId) {
        setMovePartsError("Unable to resolve source bin. Please try again.");
        setIsMovingParts(false);
        return;
      }

      await Promise.all(
        validRows.map((row) =>
          WarehouseBinsApi.api.moveParts({
            partId: movePartsData.partId ?? 0,
            sourceBinId: Number(movePartsData.sourceBinId),
            destinationBinId: Number(row.binId),
            quantity: Number(row.quantity),
          })
        )
      );
      // After successful move, refresh data
      handleCloseMoveParts();
      setListRefreshTrigger((prev) => prev + 1);
      setSuccessToast("Parts Moved!");

      // If a row is expanded, refresh its details from the expanded API
      try {
        if (expandedBinId !== null) {
          const expandedRow = rows.find((r) => r.id === expandedBinId);
          if (expandedRow && expandedRow.binId) {
            setExpandedLoading(true);
            const resp = await WarehouseBinsApi.api.getExpandedBinParts(Number(expandedRow.binId));
            let parts: any[] = [];
            if (Array.isArray(resp)) {
              parts = resp;
            } else if (Array.isArray(resp?.data)) {
              parts = resp.data;
            } else if (Array.isArray(resp?.data?.data)) {
              parts = resp.data.data;
            } else if (Array.isArray(resp?.content)) {
              parts = resp.content;
            } else if (Array.isArray(resp?.data?.content)) {
              parts = resp.data.content;
            }

            const products = (parts || []).map((item: any, idx: number) => ({
              id: `${expandedRow.binId}-${idx}`,
              productName: item.partName ?? item.productName ?? "",
              productCode: item.referenceCode ?? item.productCode ?? "",
              referenceCode: item.referenceCode ?? item.productCode ?? "",
              quantity: item.quantity ?? 0,
              warehouseId: item.warehouseId ?? expandedRow.warehouseId,
              binId: expandedRow.binId,
              partId: item.partId ?? item.id,
              binName: expandedRow.binName,
            }));

            setExpandedProducts(products);
            setExpandedLoadError(null);
            setExpandedLoading(false);
          }
        }
      } catch (err) {
        setExpandedLoadError("Failed to refresh expanded bin details.");
        setExpandedLoading(false);
      }
    } catch (err: any) {
      const backendMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to move parts. Please try again.";
      setMovePartsError(backendMessage);
    } finally {
      setIsMovingParts(false);
    }
  };

  return (
    <div className="flex min-h-[calc(100vh-64px)] bg-slate-50">
      <div className="hidden w-64 shrink-0 bg-white shadow-xl lg:block">
        <SideBarNavigation />
      </div>

      <main className="flex-1 px-4 py-5 sm:px-6 lg:px-10">
        <div className="mx-auto max-w-[1320px]">
          <div className="mb-4 flex items-center justify-between gap-4">
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
              Part Bin Mapping
            </h1>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleDownloadTemplate}
                disabled={isDownloadingTemplate}
                className="inline-flex items-center gap-2 rounded-full border border-blue-600 px-4 py-2 text-sm font-semibold text-blue-600 transition hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50">
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v12m0 0l-4-4m4 4l4-4M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                </svg>
                {isDownloadingTemplate ? "Downloading..." : "Download"}
              </button>

              <input
                ref={bulkFileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setIsUploadingBulk(true);
                  setBulkUploadError(null);
                  try {
                    const response = await WarehouseBinsApi.api.bulkUploadBins(file);
                    
                    if (response.success) {
                      setIsBulkUploadModalOpen(false);
                      setSuccessToast(response.message || "Bins Uploaded!");
                    } else if (response.hasErrorFile) {
                      setBulkUploadError(
                        response.message ||
                          "Some records could not be processed. Check the downloaded error file."
                      );
                    }

                    setCurrentPage(0);
                  } catch (err: any) {
                    const serverMessage =
                      err?.response?.data?.message ||
                      err?.response?.data?.error ||
                      err?.message ||
                      "Failed to upload file. Please try again.";
                    setBulkUploadError(serverMessage);
                  } finally {
                    setIsUploadingBulk(false);
                    if (bulkFileInputRef.current) bulkFileInputRef.current.value = "";
                  }
                }}
              />

              <button
                type="button"
                onClick={() => setIsBulkUploadModalOpen(true)}
                disabled={isUploadingBulk}
                className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 12V4m0 0L8 8m4-4l4 4" />
                </svg>
                {isUploadingBulk ? "Uploading..." : "Bulk Upload"}
              </button>
            </div>
          </div>

          <p className="mb-4 text-left text-[11px] font-medium text-slate-400">
            Dashboard &gt; <span className="text-slate-600">Bins Management</span>
          </p>

          <section className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-[0_1px_8px_rgba(15,23,42,0.05)]">
            <div className="text-left border-b border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700">
              Warehouse ID: {defaultWarehouseIdLabel}
              {defaultWarehouseNameLabel && defaultWarehouseNameLabel !== "-" ? (
                <span className="ml-2 text-sm font-normal text-slate-500">({defaultWarehouseNameLabel})</span>
              ) : null}
            </div>

            <div className="flex flex-col gap-3 px-4 py-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-center gap-3">
                <label className="text-sm font-semibold text-slate-700" htmlFor="status-filter">
                  Status:
                </label>
                <div className="relative group">
                  <select
                    id="status-filter"
                    value={statusFilter}
                    onChange={(event) => setStatusFilter(event.target.value)}
                    className="h-10 min-w-[120px] appearance-none rounded-xl border border-slate-200 bg-white pl-3 pr-8 text-sm text-slate-900 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100">
                    <option value="All">All</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                  <svg
                    className="pointer-events-none absolute right-2.5 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 9l6 6 6-6" />
                  </svg>
                </div>
              </div>

              <div className="relative w-full sm:max-w-[360px]">
                <svg
                  className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor">
                  <circle cx="11" cy="11" r="7" strokeWidth={2} />
                  <path d="M20 20l-3.5-3.5" strokeWidth={2} strokeLinecap="round" />
                </svg>
                <input
                  value={searchQuery}
                  onChange={(event) => {
                    setSearchQuery(event.target.value);
                    setExpandedBinId(null);
                  }}
                  placeholder="Search"
                  className="h-10 w-full rounded-xl border border-slate-200 bg-white pl-11 pr-4 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                />
              </div>
            </div>

            {loadError && (
              <div className="border-t border-slate-100 px-4 py-3 text-sm text-rose-600">{loadError}</div>
            )}

            <div className="overflow-x-auto border-t border-slate-100">
              <table className="min-w-full border-collapse text-left">
                <thead className="bg-slate-50">
                  <tr className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                    <th className="w-12 px-4 py-4">#</th>
                    <th className="px-4 py-4">Bin Name</th>
                    <th className="px-4 py-4">Part&apos;s Count</th>
                    <th className="px-4 py-4">Created At</th>
                    <th className="px-4 py-4">Status</th>
                    <th className="w-16 px-4 py-4">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row, index) => {
                    const isExpanded = expandedBinId === row.id;
                    const isInactive = row.status !== "Active";

                    return (
                      <Fragment key={row.id}>
                        <tr
                          className={`border-t border-slate-100 text-sm text-slate-700 ${
                            isExpanded ? "bg-blue-50/30" : ""
                          }`}>
                          <td className="px-4 py-4">{currentPage * ROWS_PER_PAGE + index + 1}</td>
                          <td className="px-4 py-4 font-medium text-slate-900">{row.binName}</td>
                          <td className="px-4 py-4">{row.partsCount}</td>
                          <td className="px-4 py-4">{row.createdAt}</td>
                          <td className="px-4 py-4">
                            <span
                              className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                                row.status === "Active"
                                  ? "bg-emerald-50 text-emerald-500"
                                  : "bg-rose-50 text-rose-500"
                              }`}>
                              {row.status}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-right">
                            <button
                              type="button"
                              onClick={async () => {
                                if (isInactive) return;
                                const wasExpanded = expandedBinId === row.id;
                                if (wasExpanded) {
                                  setExpandedBinId(null);
                                  setExpandedProducts(null);
                                  setExpandedLoadError(null);
                                  return;
                                }

                                // expand: fetch fresh data for this bin if we have a real binId
                                setExpandedBinId(row.id);
                                setExpandedLoadError(null);
                                if (row.binId) {
                                  setExpandedLoading(true);
                                  try {
                                    // Call API to fetch expanded bin parts
                                    const resp = await WarehouseBinsApi.api.getExpandedBinParts(Number(row.binId));
                                    // API returns an object like: { code, message, data: [ ... ] }
                                    // Support a few possible shapes defensively.
                                    let parts: any[] = [];
                                    if (Array.isArray(resp)) {
                                      parts = resp;
                                    } else if (Array.isArray(resp?.data)) {
                                      parts = resp.data;
                                    } else if (Array.isArray(resp?.data?.data)) {
                                      parts = resp.data.data;
                                    } else if (Array.isArray(resp?.content)) {
                                      parts = resp.content;
                                    } else if (Array.isArray(resp?.data?.content)) {
                                      parts = resp.data.content;
                                    } else {
                                      parts = [];
                                    }

                                    const products = (parts || []).map((item: any, idx: number) => ({
                                      id: `${row.binId}-${idx}`,
                                      productName: item.partName ?? item.productName ?? "",
                                      productCode: item.referenceCode ?? item.productCode ?? "",
                                      referenceCode: item.referenceCode ?? item.productCode ?? "",
                                      quantity: item.quantity ?? 0,
                                      warehouseId: item.warehouseId ?? row.warehouseId,
                                      binId: row.binId,
                                      partId: item.partId ?? item.id,
                                      binName: row.binName,
                                    }));
                                    setExpandedProducts(products);
                                  } catch (err) {
                                    setExpandedLoadError("Failed to load bin details. Please try again.");
                                    setExpandedProducts(row.products ?? null);
                                  } finally {
                                    setExpandedLoading(false);
                                  }
                                } else {
                                  setExpandedProducts(row.products ?? null);
                                }
                              }}
                              disabled={isInactive}
                              aria-expanded={isExpanded}
                              aria-label={isInactive ? "Inactive bin details disabled" : isExpanded ? "Collapse details" : "Expand details"}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:bg-blue-50 hover:border-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white disabled:hover:border-slate-200">
                              <svg
                                className={`h-4 w-4 transform transition-transform duration-200 ${isExpanded ? "rotate-180" : "rotate-0"}`}
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
                              </svg>
                            </button>
                          </td>
                        </tr>

                        {isExpanded && (expandedLoading || expandedProducts) && (
                          <tr key={`${row.id}-details`}>
                            <td colSpan={6} className="px-0 py-0">
                              <div className="m-0 border-2 border-blue-500 bg-white px-4 py-3">
                                <table className="min-w-full border-collapse text-left">
                                  <thead>
                                    <tr className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
                                      <th className="px-4 py-2">#</th>
                                      <th className="px-4 py-2">Product Name</th>
                                      <th className="px-4 py-2">Part Id</th>
                                      <th className="px-4 py-2">Reference Code</th>
                                      <th className="px-4 py-2">Quantity</th>
                                      <th className="px-4 py-2" />
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {expandedLoading && (
                                      <tr>
                                        <td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-500">Loading details...</td>
                                      </tr>
                                    )}
                                    {!expandedLoading && (expandedProducts ?? row.products ?? []).map((product, productIndex) => (
                                      <tr key={product.id} className="border-t border-slate-100 text-sm text-slate-700">
                                        <td className="px-4 py-3">{productIndex + 1}</td>
                                        <td className="px-4 py-3 font-medium text-slate-900">{product.productName}</td>
                                        <td className="px-4 py-3">{product.partId ?? "-"}</td>
                                        <td className="px-4 py-3">{product.referenceCode}</td>
                                        <td className="px-4 py-3">{product.quantity}</td>
                                        <td className="px-4 py-3 text-right">
                                          <button
                                            type="button"
                                            onClick={() => handleOpenMoveParts(product, row.binName)}
                                            className="rounded-full bg-emerald-500 px-4 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-emerald-600">
                                            Move Parts
                                          </button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                                {expandedLoadError && (
                                  <p className="mt-3 text-sm text-rose-600">{expandedLoadError}</p>
                                )}
                                <div className="mt-3 flex justify-start">
                                  <button
                                    type="button"
                                    onClick={() => handleOpenAddProduct(row)}
                                    className="inline-flex items-center gap-2 rounded-full border border-blue-500 bg-white px-4 py-2 text-sm font-semibold text-blue-600 transition hover:bg-blue-50">
                                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v14m-7-7h14" />
                                    </svg>
                                    Add
                                  </button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}

                  {isLoading && (
                    <tr>
                      <td colSpan={6} className="px-4 py-16 text-center text-sm text-slate-500">
                        Loading bins...
                      </td>
                    </tr>
                  )}

                  {!isLoading && filteredRows.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-16 text-center text-sm text-slate-500">
                        No bins found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-4">
              <button
                type="button"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
                disabled={currentPage === 0}
                className="rounded-md px-2 py-1 text-slate-500 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50">
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              {pageNumbers.map((pageNumber) => (
                <button
                  key={pageNumber}
                  type="button"
                  onClick={() => setCurrentPage(pageNumber - 1)}
                  className={`h-7 w-7 rounded-sm text-xs font-semibold ${
                    currentPage === pageNumber - 1
                      ? "bg-blue-600 text-white"
                      : "text-slate-700 hover:bg-slate-100"
                  }`}>
                  {pageNumber}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1))}
                disabled={currentPage >= totalPages - 1}
                className="rounded-md px-2 py-1 text-slate-500 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50">
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </section>
        </div>
      </main>

      {movePartsData && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={(e) => { if (e.target === e.currentTarget) handleCloseMoveParts(); }}>
          <div className="relative w-full max-w-3xl rounded-2xl bg-white px-8 py-8 shadow-2xl">

            <button
              type="button"
              onClick={handleCloseMoveParts}
              className="absolute right-5 top-5 text-slate-400 transition hover:text-slate-600">
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <h2 className="text-left mb-6 text-xl font-bold text-slate-900">
              Warehouse ID - {movePartsData.warehouseId}
              {movePartsData.sourceBinName ? (
                <span className="ml-2 text-sm font-normal text-slate-400">(Bin Name: {movePartsData.sourceBinName})</span>
              ) : null}
            </h2>

            <div className="text-left mb-6 grid grid-cols-3 gap-6 border-b border-slate-200 pb-6">
              {([
                { label: "Part Name", value: movePartsData.productName },
                { label: "Reference Code", value: movePartsData.referenceCode },
                { label: "Quantity", value: String(movePartsData.quantity) },
              ] as const).map(({ label, value }) => (
                <div key={label}>
                  <p className="mb-2 text-[11px] font-medium text-slate-400">{label}</p>
                  <p className="border-b border-slate-200 pb-2 text-sm text-slate-800">{value}</p>
                </div>
              ))}
            </div>

            <div className="text-left mb-2 grid grid-cols-[48px_1fr_1fr_40px] gap-4 border-b border-slate-200 pb-2 text-[11px] font-medium text-slate-400">
              <span>#</span>
              <span>Select Bin</span>
              <span>Enter Quantity</span>
              <span />
            </div>

            <div className="text-left mb-4">
              {moveBinRows.map((row, index) => (
                <div
                  key={row.rowId}
                  className="grid grid-cols-[48px_1fr_1fr_40px] items-center gap-4 border-b border-slate-100 py-3">
                  <span className="text-sm text-slate-700">{index + 1}</span>
                  <ScrollableBinSelect
                    value={row.binId}
                    onChange={(binId) => handleBinRowChange(row.rowId, "binId", binId)}
                    options={binOptions}
                    isLoading={isBinsLoading}
                    isLoadingMore={isActiveBinsLoadingMore}
                    isSearching={isActiveBinsSearching}
                    hasMore={activeBinsPage < activeBinsTotalPages - 1}
                    onLoadMore={handleLoadMoreActiveBins}
                    searchQuery={moveModalBinSearchQuery}
                    onSearchChange={setMoveModalBinSearchQuery}
                    excludeBinId={movePartsData.sourceBinId}
                    error={index === 0 ? activeBinsError : null}
                  />
                  <input
                    type="number"
                    min="1"
                    step="1"
                    inputMode="numeric"
                    pattern="\d*"
                    value={row.quantity}
                    onChange={(e) => handleBinRowChange(row.rowId, "quantity", e.target.value)}
                    placeholder="Enter Quantity"
                    className="border-0 border-b border-slate-200 bg-transparent pb-1 text-sm text-slate-800 outline-none placeholder:text-slate-300 focus:border-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveBinRow(row.rowId)}
                    disabled={moveBinRows.length === 1}
                    className="flex items-center justify-center text-rose-400 transition hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-30">
                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={handleAddBinRow}
              className="mb-6 flex items-center gap-1.5 text-sm font-semibold text-blue-600 transition hover:text-blue-700">
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v14m-7-7h14" />
              </svg>
              Add Bin
            </button>

            {movePartsError && (
              <p className="mb-4 text-sm text-rose-600">{movePartsError}</p>
            )}

            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleMoveParts}
                disabled={isMovingParts || isBinsLoading}
                className="rounded-full bg-blue-600 px-10 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">
                {isMovingParts ? "Moving..." : "Move Parts"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isAddProductOpen && addProductBin && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={(e) => { if (e.target === e.currentTarget) handleCloseAddProduct(); }}>
          <div className="relative w-full max-w-md rounded-2xl bg-white px-8 py-8 shadow-2xl">
            <button
              type="button"
              onClick={handleCloseAddProduct}
              className="absolute right-5 top-5 text-slate-400 transition hover:text-slate-600">
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <h2 className="text-left mb-4 text-lg font-bold text-slate-900">Add Product to {addProductBin.binName}</h2>

            <div className="mb-4">
              <label className="mb-2 block text-left text-[11px] font-medium text-slate-400">Product</label>
              <select
                value={selectedProductId}
                onChange={(e) => setSelectedProductId(e.target.value)}
                disabled={isProductsLoading}
                className="h-10 w-full border-0 border-b border-slate-200 bg-transparent px-0 text-sm text-slate-800 outline-none focus:border-blue-500">
                <option value="">{isProductsLoading ? "Loading products..." : "Select Product"}</option>
                {productOptions.map((p) => (
                  <option key={`${p.productId}-${p.id}`} value={p.productId}>{p.productName}</option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="mb-2 block text-left text-[11px] font-medium text-slate-400">Quantity</label>
              <input
                type="number"
                min="1"
                step="1"
                inputMode="numeric"
                pattern="\d*"
                value={addQuantity}
                onChange={(e) => setAddQuantity(String(e.target.value).split(".")[0].replace(/\D/g, ""))}
                placeholder="Enter Quantity"
                className="h-10 w-full border-0 border-b border-slate-200 bg-transparent px-0 text-sm text-slate-800 outline-none focus:border-blue-500" />
            </div>

            {addProductError && <p className="mb-4 text-sm text-rose-600">{addProductError}</p>}

            <div className="flex justify-end gap-3">
              <button type="button" onClick={handleCloseAddProduct} className="rounded-full px-6 py-2 text-sm font-semibold border border-slate-200 text-slate-700">Cancel</button>
              <button type="button" onClick={handleSubmitAddProduct} disabled={isAddingProduct} className="rounded-full bg-blue-600 px-6 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-50">{isAddingProduct ? "Adding..." : "Add"}</button>
            </div>
          </div>
        </div>
      )}

      {isBulkUploadModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsBulkUploadModalOpen(false);
          }}>
          <div className="relative w-full max-w-lg rounded-3xl bg-white p-10 shadow-2xl min-h-[320px]">
            <button
              type="button"
              onClick={() => setIsBulkUploadModalOpen(false)}
              className="absolute right-5 top-5 text-slate-400 transition hover:text-slate-600">
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="mx-auto max-w-[420px] text-center">
              <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-lg border border-blue-100 bg-blue-50">
                <svg className="h-10 w-10 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v12m0 0l-4-4m4 4l4-4M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                </svg>
              </div>

              <p className="mb-6 text-lg font-semibold text-slate-700">Please Download The Template Before Uploading The Files.</p>

              {bulkUploadError && (
                <div className="mb-4 rounded-md border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  <strong className="block font-semibold">Upload Error</strong>
                  <span className="block">{bulkUploadError}</span>
                </div>
              )}

              <div className="flex items-center justify-center gap-4">
                <button
                  type="button"
                  onClick={async () => {
                      setIsDownloadingTemplate(true);
                      try {
                        const blob = await WarehouseBinsApi.api.getBinsSampleSheet();
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = "part-bin-mapping.xlsx";
                        a.click();
                        URL.revokeObjectURL(url);
                      } catch {
                        setBulkUploadError("Failed to download template.");
                      } finally {
                        setIsDownloadingTemplate(false);
                      }
                    }}
                  className="inline-flex items-center gap-2 rounded-full border border-blue-600 px-4 py-2 text-sm font-semibold text-blue-600 transition hover:bg-blue-50">
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v12m0 0l-4-4m4 4l4-4M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                  </svg>
                  <span>Download Template</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                      // clear previous error and open file picker; keep modal open during upload
                      setBulkUploadError(null);
                      bulkFileInputRef.current?.click();
                    }}
                  className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700">
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 12V4m0 0L8 8m4-4l4 4" />
                  </svg>
                  <span>Upload File</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {successToast && (
        <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
          <div className="pointer-events-auto overflow-hidden rounded-xl bg-white shadow-2xl w-56">
            <div className="flex flex-col items-center gap-3 px-6 py-8">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500">
                <svg className="h-7 w-7 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-sm font-medium text-slate-700">{successToast}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageBinsPage;